import React, { useState, useCallback, useRef } from "react";
import { api, formatApiError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { EPCBadge } from "../components/EPCBadge";
import { Inbox, UploadCloud, Loader2, CheckCircle2, AlertTriangle, FileSpreadsheet, ArrowRight, ArrowUpRight, FileX, Building2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

export default function JobCardInboxPage() {
  const [files, setFiles] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState(null);
  const [summary, setSummary] = useState(null);
  const fileInputRef = useRef(null);

  const handleFiles = useCallback((picked) => {
    const arr = Array.from(picked || [])
      .filter((f) => /\.(xlsx|xlsm|csv|pdf)$/i.test(f.name))
      .slice(0, 25);
    if (arr.length === 0) {
      toast.error("Add .xlsx, .xlsm, .csv or .pdf files");
      return;
    }
    setFiles(arr);
    setResults(null);
    setSummary(null);
  }, []);

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const upload = async () => {
    if (files.length === 0) return;
    setUploading(true);
    setResults(null);
    setSummary(null);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const { data } = await api.post("/jobcard-inbox?replace=true", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      setResults(data.results);
      setSummary(data.summary);
      toast.success(`${data.summary.routed} of ${data.summary.total} job cards routed`, {
        description:
          data.summary.unmatched + data.summary.failed > 0
            ? `${data.summary.unmatched} unmatched · ${data.summary.failed} failed — see the list below`
            : "All files matched to properties successfully",
      });
    } catch (e) {
      toast.error("Inbox upload failed", { description: formatApiError(e?.response?.data?.detail) || e.message });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="p-8 space-y-6 fade-up max-w-5xl" data-testid="jobcard-inbox-page">
      <div>
        <div className="text-xs uppercase tracking-[0.25em]" style={{ color: "var(--hi-orange)" }}>BULK JOB-CARD INBOX</div>
        <h1 className="font-heading font-black text-4xl mt-2 tracking-tight" style={{ color: "var(--hi-olive)" }}>
          Drop job cards.<br /><span style={{ color: "var(--hi-orange)" }}>We route them.</span>
        </h1>
        <p className="text-muted-foreground mt-2 max-w-3xl">
          Drag a folder of job cards onto the area below. We'll read the property address from each workbook
          and write the parsed measures + Pre/Post-install SAP to the matching property — no manual picking required.
        </p>
      </div>

      {/* Drop zone */}
      <Card
        className={`rounded-none border-2 border-dashed ${dragging ? "" : "border-slate-300"} p-12 text-center transition-colors`}
        style={{ borderColor: dragging ? "var(--hi-orange)" : undefined, backgroundColor: dragging ? "#fff8ed" : "var(--hi-cream)" }}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        data-testid="drop-zone"
      >
        <div className="flex flex-col items-center gap-3">
          <div className="p-4 border border-slate-300" style={{ backgroundColor: "white" }}>
            <UploadCloud className="h-10 w-10" style={{ color: "var(--hi-olive)" }} />
          </div>
          <div className="font-heading font-black text-xl" style={{ color: "var(--hi-olive)" }}>
            Drop your job cards here
          </div>
          <div className="text-xs text-muted-foreground max-w-sm">
            Up to <strong>25 files</strong> at once · Excel (.xlsx, .xlsm), CSV, or PDF · 15 MB each
          </div>
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            className="rounded-none border-slate-300 mt-2"
            data-testid="browse-files-btn"
          >
            <FileSpreadsheet className="h-4 w-4 mr-2" /> Browse files
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".xlsx,.xlsm,.csv,.pdf"
            onChange={(e) => handleFiles(e.target.files)}
            className="hidden"
            data-testid="inbox-file-input"
          />
        </div>
      </Card>

      {/* Selected files preview + go button */}
      {files.length > 0 && (
        <Card className="rounded-none border-border p-4" data-testid="selected-files-card">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs uppercase tracking-wider font-bold text-muted-foreground">
              {files.length} file(s) ready
            </div>
            <Button
              onClick={upload}
              disabled={uploading}
              className="rounded-none text-white"
              style={{ backgroundColor: "var(--hi-olive)" }}
              data-testid="process-inbox-btn"
            >
              {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Inbox className="h-4 w-4 mr-2" />}
              {uploading ? "Routing…" : "Route to properties"}
            </Button>
          </div>
          <ul className="text-xs space-y-1 font-mono" data-testid="selected-files-list">
            {files.map((f, i) => (
              <li key={`${f.name}-${i}`} className="flex items-center gap-2 text-slate-700">
                <FileSpreadsheet className="h-3 w-3" />
                <span className="truncate">{f.name}</span>
                <span className="text-slate-400">· {(f.size / 1024).toFixed(0)} KB</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* Loading skeleton */}
      {uploading && (
        <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-14" />)}</div>
      )}

      {/* Results */}
      {summary && results && (
        <div className="space-y-4" data-testid="inbox-results">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <ResultStat label="Total" value={summary.total} color="var(--hi-olive)" data-testid="stat-total" />
            <ResultStat label="Routed" value={summary.routed} color="#16a34a" data-testid="stat-routed" />
            <ResultStat label="Unmatched" value={summary.unmatched} color="var(--hi-orange)" data-testid="stat-unmatched" />
            <ResultStat label="Failed" value={summary.failed} color="#dc2626" data-testid="stat-failed" />
          </div>

          <Card className="rounded-none border-border" data-testid="results-table">
            <div className="p-3 border-b border-slate-100 text-xs uppercase tracking-wider font-bold text-muted-foreground">
              Per-file outcomes
            </div>
            <ul className="divide-y divide-slate-100">
              {results.map((r, idx) => (
                <li key={idx} className="p-4 flex items-start gap-3" data-testid={`result-row-${idx}`}>
                  <div className="mt-0.5">
                    {r.status === "routed" && <CheckCircle2 className="h-5 w-5 text-emerald-600" />}
                    {r.status === "unmatched" && <AlertTriangle className="h-5 w-5" style={{ color: "var(--hi-orange-deep)" }} />}
                    {r.status === "failed" && <FileX className="h-5 w-5 text-red-600" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-xs text-slate-600 truncate">{r.filename}</span>
                      <span
                        className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider border"
                        style={
                          r.status === "routed"
                            ? { backgroundColor: "#dcfce7", borderColor: "#22c55e", color: "#166534" }
                            : r.status === "unmatched"
                              ? { backgroundColor: "#fff8ed", borderColor: "var(--hi-orange)", color: "var(--hi-orange-deep)" }
                              : { backgroundColor: "#fee2e2", borderColor: "#f87171", color: "#991b1b" }
                        }
                      >
                        {r.status}
                      </span>
                      {r.projected_band && (
                        <span className="text-[10px] text-muted-foreground inline-flex items-center gap-1">
                          → <EPCBadge band={r.projected_band} size="sm" /> SAP {r.projected_sap}
                          {r.reached_c && <span className="text-emerald-700 font-bold">✓</span>}
                        </span>
                      )}
                    </div>
                    {r.address_on_sheet && (
                      <div className="text-xs text-slate-700 mt-1">
                        <span className="text-muted-foreground">sheet:</span> {r.address_on_sheet}
                      </div>
                    )}
                    {r.status === "routed" && (
                      <div className="text-xs text-emerald-800 mt-1 flex items-center gap-1 flex-wrap">
                        <ArrowRight className="h-3 w-3" />
                        <Link to={`/properties?selected=${r.property_id}`} className="font-medium underline-offset-2 hover:underline inline-flex items-center gap-1">
                          {r.property_address} <span className="text-muted-foreground">({r.property_postcode})</span>
                          <ArrowUpRight className="h-3 w-3" />
                        </Link>
                        <span className="text-muted-foreground">· {r.measures_count} measures applied</span>
                      </div>
                    )}
                    {r.error && (
                      <div className="text-xs text-amber-900 mt-1 leading-relaxed">{r.error}</div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      )}

      <Card className="rounded-none border-dashed border-slate-300 p-4 text-xs text-slate-600 leading-relaxed flex items-start gap-2" data-testid="inbox-howto">
        <Building2 className="h-4 w-4 mt-0.5 shrink-0" style={{ color: "var(--hi-orange)" }} />
        <span>
          <strong>How matching works.</strong> We read the "Address" cell on each workbook, extract the UK
          postcode + leading number/name, and find the property in your portfolio that matches both. If your
          workbook has no address, no postcode, or two postcodes share the same address, the file is reported as
          <em> unmatched</em> — open the property manually and upload from there.
        </span>
      </Card>
    </div>
  );
}

function ResultStat({ label, value, color, ...rest }) {
  return (
    <Card className="rounded-none border-border p-4" {...rest}>
      <div className="text-xs uppercase tracking-wider font-bold" style={{ color }}>{label}</div>
      <div className="font-heading font-black text-3xl mt-1" style={{ color }}>{value}</div>
    </Card>
  );
}
